import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getVersionHistory, createAppVersion, updateAppVersion, deleteAppVersion 
} from '../../lib/api';
import { APP_VERSION } from '../ui/UpdateNotification';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter
} from '../ui/dialog';
import { 
  ChevronLeft, Plus, Package, Loader2, Edit, Trash2, 
  CheckCircle, XCircle, AlertTriangle, Sparkles
} from 'lucide-react';
import { toast } from '../ui/sonner';

const VersionManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form dialog state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingVersion, setEditingVersion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    description: '',
    changelog: [''],
    is_critical: false
  });

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVersionHistory(50);
      setVersions(data);
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Gagal memuat data versi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const resetForm = () => {
    setFormData({
      version: '',
      title: '',
      description: '',
      changelog: [''],
      is_critical: false
    });
    setEditingVersion(null);
    setFormMode('create');
  };

  const openCreateDialog = () => {
    resetForm();
    // Suggest next version number
    if (versions.length > 0) {
      const latestVer = versions[0].version.split('.');
      const patch = parseInt(latestVer[2] || 0) + 1;
      setFormData(prev => ({
        ...prev,
        version: `${latestVer[0]}.${latestVer[1]}.${patch}`
      }));
    }
    setFormMode('create');
    setShowFormDialog(true);
  };

  const openEditDialog = (version) => {
    setFormData({
      version: version.version,
      title: version.title,
      description: version.description || '',
      changelog: version.changelog.length > 0 ? version.changelog : [''],
      is_critical: version.is_critical
    });
    setEditingVersion(version);
    setFormMode('edit');
    setShowFormDialog(true);
  };

  const addChangelogItem = () => {
    setFormData(prev => ({
      ...prev,
      changelog: [...prev.changelog, '']
    }));
  };

  const updateChangelogItem = (index, value) => {
    setFormData(prev => ({
      ...prev,
      changelog: prev.changelog.map((item, i) => i === index ? value : item)
    }));
  };

  const removeChangelogItem = (index) => {
    if (formData.changelog.length > 1) {
      setFormData(prev => ({
        ...prev,
        changelog: prev.changelog.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.version || !formData.title) {
      toast.error('Versi dan judul wajib diisi');
      return;
    }
    
    const changelog = formData.changelog.filter(item => item.trim() !== '');
    if (changelog.length === 0) {
      toast.error('Minimal satu item changelog harus diisi');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        changelog
      };
      
      if (formMode === 'create') {
        await createAppVersion(payload);
        toast.success('Versi baru berhasil dibuat!');
      } else {
        await updateAppVersion(editingVersion.id, {
          title: payload.title,
          description: payload.description,
          changelog: payload.changelog,
          is_critical: payload.is_critical
        });
        toast.success('Versi berhasil diupdate!');
      }
      
      setShowFormDialog(false);
      resetForm();
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan versi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (version) => {
    if (!window.confirm(`Yakin ingin menghapus versi ${version.version}?`)) return;
    
    try {
      await deleteAppVersion(version.id);
      toast.success('Versi berhasil dihapus');
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus versi');
    }
  };

  const handleActivate = async (version) => {
    try {
      await updateAppVersion(version.id, { is_active: true });
      toast.success(`Versi ${version.version} diaktifkan`);
      fetchVersions();
    } catch (error) {
      toast.error('Gagal mengaktifkan versi');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Redirect if not admin
  if (user?.role !== 'admin_dinas') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold">Akses Ditolak</h2>
            <p className="text-slate-500 mt-2">Hanya Admin yang dapat mengakses halaman ini</p>
            <Button className="mt-4" onClick={() => navigate(-1)}>Kembali</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Kembali
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Package className="w-8 h-8 text-indigo-600" />
              Kelola Versi Aplikasi
            </h1>
            <p className="text-slate-600 mt-1">
              Kelola changelog dan notifikasi update untuk pengguna PWA
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Versi Frontend saat ini: <Badge variant="outline">v{APP_VERSION}</Badge>
            </p>
          </div>
          
          <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Rilis Versi Baru
          </Button>
        </div>
      </div>

      {/* Version List */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : versions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Belum ada versi yang dirilis</p>
              <p className="text-sm text-slate-400 mt-1">Klik "Rilis Versi Baru" untuk membuat changelog pertama</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {versions.map((version) => (
              <Card key={version.id} className={`${version.is_active ? 'border-indigo-300 bg-indigo-50/50' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-xl font-bold text-slate-800">
                          v{version.version}
                        </span>
                        {version.is_active && (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Aktif
                          </Badge>
                        )}
                        {version.is_critical && (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Critical
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-semibold text-slate-700">{version.title}</h3>
                      {version.description && (
                        <p className="text-sm text-slate-500 mt-1">{version.description}</p>
                      )}
                      
                      {/* Changelog */}
                      <div className="mt-3 p-3 bg-slate-100 rounded-lg">
                        <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Changelog:
                        </p>
                        <ul className="space-y-1">
                          {version.changelog.map((item, idx) => (
                            <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="text-slate-400">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <p className="text-xs text-slate-400 mt-3">
                        Dibuat oleh {version.created_by_nama} • {formatDate(version.created_at)}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      {!version.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleActivate(version)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Aktifkan
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(version)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      {!version.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(version)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create' ? 'Rilis Versi Baru' : 'Edit Versi'}
            </DialogTitle>
            <DialogDescription>
              Tambahkan informasi changelog untuk pengguna
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Version Number */}
            <div className="space-y-2">
              <Label htmlFor="version">Nomor Versi *</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({...prev, version: e.target.value}))}
                placeholder="Contoh: 2.1.0"
                disabled={formMode === 'edit'}
              />
              <p className="text-xs text-slate-500">Format: X.Y.Z (Major.Minor.Patch)</p>
            </div>
            
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Judul Update *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                placeholder="Contoh: Fitur Surat Tugas"
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Deskripsi (opsional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Deskripsi singkat tentang update ini..."
                rows={2}
              />
            </div>
            
            {/* Changelog Items */}
            <div className="space-y-2">
              <Label>Changelog *</Label>
              <div className="space-y-2">
                {formData.changelog.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={item}
                      onChange={(e) => updateChangelogItem(index, e.target.value)}
                      placeholder={`Perubahan ${index + 1}...`}
                    />
                    {formData.changelog.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeChangelogItem(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addChangelogItem}
              >
                <Plus className="w-4 h-4 mr-1" />
                Tambah Item
              </Button>
            </div>
            
            {/* Critical Update Toggle */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div>
                <Label className="text-amber-800">Update Kritis</Label>
                <p className="text-xs text-amber-600">
                  Jika aktif, pengguna tidak bisa melewati notifikasi update
                </p>
              </div>
              <Switch
                checked={formData.is_critical}
                onCheckedChange={(checked) => setFormData(prev => ({...prev, is_critical: checked}))}
              />
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowFormDialog(false)}
              >
                Batal
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {formMode === 'create' ? 'Rilis Versi' : 'Simpan Perubahan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VersionManagementPage;
